import type { FC } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Unstable_Grid2";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { RouterLink } from "src/components/router-link";
import { useCookieConsent } from "src/contexts/cookie-consent-context";
import { paths } from "src/paths";
import { CertSeal } from "./certseal";
import { useTranslation } from "react-i18next";

interface Section {
  title: string;
  items: {
    external?: boolean;
    title: string;
    path?: string;
    onClick?: () => void;
  }[];
}

export const Footer: FC = (props) => {
  const { t } = useTranslation();
  const { openBanner } = useCookieConsent();

  const sections: Section[] = [
    {
      title: t("Legal"),
      items: [
        {
          title: t("Terms & Conditions"),
          path: paths.legalnotice,
        },
        {
          title: t("Data Privacy"),
          path: paths.dataprivacy,
        },
        {
          title: t("Privacy Policy"),
          path: paths.privacyPolicy,
        },
        {
          title: t("cookiePolicy", "Cookie Policy"),
          path: paths.cookiePolicy,
        },
        {
          title: t("cookieSettings", "Cookie settings"),
          onClick: openBanner,
        },
      ],
    },
    {
      title: t("Quick links"),
      items: [
        {
          title: t("Login"),
          path: "/auth/firebase/login",
        },
        {
          title: t("About Us"),
          path: paths.aboutus,
        },
        {
          title: t("Blog"),
          path: "https://medium.com/@opensourceclinic",
        },
        {
          title: t("Donate"),
          path: paths.donate,
        },
        {
          title: t("Join our team"),
          path: "https://wa.me/31639202608",
        },
        {
          title: t("Apps"),
          path: paths.diabetesmanagementapps,
        },
        {
          title: t("Services and pricing"),
          path: paths.pricing,
        },
      ],
    },
    {
      title: t("Contact"),
      items: [
        {
          title: t("Email"),
          path: "mailto: info@opensource.clinic",
        },
        {
          title: "WhatsApp",
          path: "https://wa.me/31639202608",
        },
      ],
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "neutral.800" : "neutral.50",
        borderTopColor: "divider",
        borderTopStyle: "solid",
        borderTopWidth: 1,
        pb: 6,
        pt: {
          md: 15,
          xs: 6,
        },
      }}
      {...props}
    >
      <Container maxWidth="lg">
        <Grid container spacing={3}>
          <Grid
            xs={12}
            sm={4}
            md={(1 / 5) * 12}
            sx={{
              order: {
                xs: 4,
                md: 1,
              },
            }}
          >
            <Stack spacing={1}>
              <Stack
                alignItems="center"
                component={RouterLink}
                direction="row"
                display="inline-flex"
                href={paths.index}
                spacing={1}
                sx={{ textDecoration: "none" }}
              >
                {/* <Logo /> */}
                <Box
                  sx={{
                    color: "text.primary",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 14,
                    fontWeight: 800,
                    letterSpacing: "0.3px",
                    lineHeight: 2.5,
                    "& span": {
                      color: "primary.main",
                    },
                  }}
                >
                  Opensource.clinic
                </Box>
              </Stack>
              <Typography color="text.secondary" variant="caption">
                Medical Progress
              </Typography>
              <Typography color="text.secondary" variant="caption">
                KVK 77359089
              </Typography>
              <Link
                href="https://zoeken.bigregister.nl/zorgverlener/ea35cb3f-53a3-4247-b9f1-7e47e4692827"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "text.secondary",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                    color: "primary.main",
                  },
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Typography variant="caption" component="span">
                  {t("Medical License")}
                </Typography>
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.7rem",
                    opacity: 0.7,
                  }}
                >
                  ↗
                </Box>
              </Link>
              <Typography color="text.secondary" variant="caption">
                {t("The Netherlands")}
              </Typography>
              <br />
              <Typography
                variant="caption"
                sx={{
                  // make invisible
                  color: "transparent",
                }}
              >
                {t(
                  "Low price Nightscout Hosting, Open-Source Diabetes Care, Remote Diabetes Management, and Personalized Diabetes Coaching"
                )}
              </Typography>
            </Stack>
          </Grid>
          <Grid
            xs={12}
            sm={4}
            md={(1 / 5) * 12}
            sx={{
              order: {
                xs: 4,
                md: 2,
              },
            }}
          >
            <Stack
              sx={{
                mt: 2,
              }}
            >
              <CertSeal />
            </Stack>
          </Grid>
          {sections.map((section, index) => (
            <Grid
              key={section.title}
              xs={12}
              sm={4}
              md={(1 / 5) * 12}
              sx={{
                order: {
                  md: index + 2,
                  xs: index + 1,
                },
              }}
            >
              <Typography color="text.secondary" variant="overline">
                {section.title}
              </Typography>
              <Stack
                component="ul"
                spacing={1}
                sx={{
                  listStyle: "none",
                  m: 0,
                  p: 0,
                }}
              >
                {section.items.map((item) => {
                  const linkProps = item.onClick
                    ? {
                        component: "button" as const,
                        type: "button" as const,
                        onClick: item.onClick,
                        sx: {
                          background: "none",
                          border: 0,
                          p: 0,
                          cursor: "pointer",
                          font: "inherit",
                          textAlign: "left" as const,
                        },
                      }
                    : item.path
                    ? item.external
                      ? {
                          component: "a",
                          href: item.path,
                          target: "_blank",
                        }
                      : {
                          component: RouterLink,
                          href: item.path,
                        }
                    : {};

                  return (
                    <Stack
                      alignItems="center"
                      direction="row"
                      key={item.title}
                      spacing={2}
                    >
                      <Box
                        sx={{
                          backgroundColor: "primary.main",
                          height: 2,
                          width: 12,
                        }}
                      />
                      <Link
                        color="text.primary"
                        variant="subtitle2"
                        {...linkProps}
                      >
                        {item.title}
                      </Link>
                    </Stack>
                  );
                })}
              </Stack>
            </Grid>
          ))}

          {/* <Grid
          xs={12}
          sm={4}
          md={(1/5)*12}
          sx={{
            order: {
              xs: 4,
              md: 1,
            },
          }}
        >
          <Typography color="text.secondary" variant="overline">
            Address
          </Typography>
          <Stack
            component="ul"
            spacing={1}
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
            }}
          >
            <Typography color="text.primary" variant="subtitle2">
              {"Berberisweg 398 3053LR Rotterdam The Netherlands"}
            </Typography>
          </Stack>
        </Grid> */}
        </Grid>
        <Divider sx={{ my: 6 }} />
      </Container>
    </Box>
  );
};
